import { z } from 'zod';
import { befretDeliveryMethodSchema, dpdServiceTypeSchema, shipmentCategorySchema, shipmentEnvironmentSchema, shipmentTypeSchema, } from '../shared/shipment-category.js';
import { draftAddressSchema, draftContactSchema, draftDimensionsSchema, draftParcelItemSchema, draftPickupPointSchema, draftPromotionPreviewSchema, draftReceiverContactSchema, receiverStreetIsRequired, } from './shipment-input.schema.js';
import { isoDateSchema, shipmentTimestampsSchema } from '../shared/firestore-timestamp.schema.js';
import { currencyCodeSchema, shipmentBusinessModelSchema, supportedLanguageSchema, transportProviderSchema, } from '../shared/shipment-primitives.schema.js';
import { shipmentPhaseProgressSchema, shipmentPhaseSchema, shipmentStatusSchema, shipmentStatusDetailsSchema, } from '../shared/shipment-status.js';
import { userLocationInputSchema } from './user-location.schema.js';
/** Le brouillon ne peut commencer que dans l’état initial propre à son parcours. */
const initialStatusSchema = shipmentStatusDetailsSchema
    .extend({
    current: shipmentStatusSchema.extract(['ready_for_deposit', 'heavy_request_received']),
    isTerminal: z.literal(false),
})
    .omit({ nextActions: true, updatedAt: true })
    .strict();
const parcelInfoSchema = z
    .object({
    weight: z.number().finite().positive(),
    dimensions: draftDimensionsSchema.optional(),
    description: z.string().trim().min(1),
    contentType: z.string().trim().min(1).optional(),
    deliveryMethod: z.string().min(1),
    items: z.array(draftParcelItemSchema).optional(),
    value: z
        .object({
        amount: z.number().finite().nonnegative(),
        currency: currencyCodeSchema,
        declared: z.literal(true),
        insured: z.literal(false),
    })
        .strict()
        .optional(),
    options: z
        .object({
        reinforcedPackaging: z.boolean(),
        fragileHandling: z.boolean(),
        specialInstructions: z.string().trim().min(1).optional(),
        requiresSignature: z.literal(true),
        saturdayDelivery: z.literal(false),
    })
        .strict(),
})
    .strict();
/** Branche standard avant Stripe et DPD : aucun identifiant serveur fictif n’est persisté. */
export const standardShipmentDataDraftSchema = z
    .object({
    serviceType: dpdServiceTypeSchema,
    befretDeliveryMethod: befretDeliveryMethodSchema,
    pickupPoint: draftPickupPointSchema.optional(),
    collectionScheduled: z
        .object({
        scheduledDate: z.string().min(1),
        // #1728 F — facultatif : le tunnel standard n'a AUCUN sélecteur de
        // créneau. Exiger le champ obligeait le builder à en inventer un.
        timeWindow: z.string().min(1).optional(),
        address: draftAddressSchema,
        status: z.literal('scheduled'),
        instructions: z.string(),
    })
        .strict()
        .optional(),
    pricing: z
        .object({
        basePrice: z.number().finite().positive(),
        weightSurcharge: z.literal(0),
        serviceFee: z.literal(0),
        taxes: z.literal(0),
        total: z.number().finite().positive(),
        currency: z.literal('EUR'),
        paymentMethod: z.literal('card'),
    })
        .strict(),
})
    .strict();
/** Données minimales nécessaires pour ouvrir l’analyse et le devis d’un colis lourd. */
export const heavyShipmentDataDraftSchema = z
    .object({
    processingInfo: z
        .object({ processingType: z.literal('custom_shipping'), processingStarted: isoDateSchema })
        .strict(),
    customRequirements: z.array(z.string()),
    documents: z
        .object({
        assessmentPhotos: z.array(z.string()),
        packagingPhotos: z.array(z.string()),
        deliveryPhotos: z.array(z.string()),
    })
        .strict(),
    communications: z.array(z.never()),
    backofficeInfo: z
        .object({
        internalNotes: z.array(z.never()),
        specialRequirements: z.array(z.string()),
        flags: z
            .object({
            needsSpecialHandling: z.literal(true),
            requiresInstallation: z.literal(false),
            requiresDisassembly: z.literal(false),
            hasHazardousMaterials: z.literal(false),
            requiresPermits: z.literal(false),
        })
            .strict(),
    })
        .strict(),
})
    .strict();
/** Aperçu non autoritaire des promotions transmis pour audit et revalidation serveur. */
const promotionsSchema = z
    .object({
    applied: z.literal(true),
    codes: z.array(z.string()),
    automaticPromotions: z.array(z.string()),
    totalDiscount: z.number().finite().nonnegative(),
    originalPrice: z.number().finite().nonnegative(),
    finalPrice: z.number().finite().nonnegative(),
    appliedAt: isoDateSchema,
    details: z.array(draftPromotionPreviewSchema),
    authoritative: z.literal(false),
})
    .strict();
const metadataSchema = z
    .object({
    version: z.literal('1.0'),
    source: z.literal('web_form'),
    environment: shipmentEnvironmentSchema,
    flags: z
        .object({
        testShipment: z.literal(false),
        mockData: z.literal(false),
        archived: z.literal(false),
        migrated: z.literal(false),
        validated: z.literal(true),
    })
        .strict(),
    tags: z.array(z.string()),
})
    .strict();
/**
 * Tronc commun produit pour les deux catégories. Il contient uniquement ce qui
 * existe au moment de la création ; les enrichissements Stripe, DPD et backoffice
 * appartiennent aux schémas lifecycle.
 */
export const commonShipmentDraftSchema = z.object({
    id: z.string().min(1),
    trackingNumber: z.string().min(1),
    userId: z.string().min(1),
    type: shipmentTypeSchema,
    businessModel: shipmentBusinessModelSchema,
    transportProvider: transportProviderSchema,
    timestamps: shipmentTimestampsSchema,
    status: initialStatusSchema,
    phase: shipmentPhaseProgressSchema,
    /**
     * Phase RACINE (#1401) — le SEUL champ que les listes savent interroger
     * (`where('currentPhase','in',…)`, filtrage côté serveur : Firestore ne sait
     * pas interroger une chaîne de replis).
     *
     * Un brouillon qui n'en porte pas est **invisible de tous les onglets** dès
     * sa création, et le reste jusqu'à sa première transition — soit toute la
     * fenêtre « payé, pas encore collecté ». C'est ce qui a rendu 165 colis de
     * production invisibles (#1381) ; mesuré le 2026-08-24, **3 colis créés le
     * jour même en production n'en portaient toujours pas** : le stock se
     * reconstituait, deux jours après le backfill.
     *
     * Sa valeur se dérive de `status.current` et coïncide donc avec
     * `status.phase` à la création — ce champ n'est pas une seconde décision,
     * c'est la même, écrite là où les listes savent la lire.
     */
    currentPhase: shipmentPhaseSchema.extract(['dpd_collection', 'heavy_processing']),
    statusHistory: z.array(z.never()),
    customerInfo: z
        .object({
        sender: draftContactSchema,
        // #1706 — la rue du destinataire n'est exigée qu'en livraison à domicile
        // (règle appliquée par le `superRefine` de `unifiedShipmentDraftSchema`).
        // Sans cet assouplissement ici, la frontière accepterait un retrait au
        // point relais que le REPARSE de `buildShipmentDraft` refuserait ensuite :
        // le refus se serait simplement déplacé du client vers le serveur.
        receiver: draftReceiverContactSchema,
        preferences: z
            .object({ language: supportedLanguageSchema, timezone: z.string().min(1) })
            .strict(),
    })
        .strict(),
    parcelInfo: parcelInfoSchema,
    promotions: promotionsSchema.optional(),
    metadata: metadataSchema,
    userLocation: userLocationInputSchema.optional(),
    /**
     * #2191 — l'envoi que celui-ci remplace (« Modifier ma commande »), déjà
     * vérifié par `createShipmentDraft` : même compte. `createInvoice` remonte ce
     * lien pour expirer la session Stripe encore ouverte de l'ancienne version.
     */
    replacesShipmentId: z.string().min(1).optional(),
});
// La catégorie sert de discriminant, car elle détermine à la fois le modèle de paiement,
// le transporteur, le statut initial et la branche de données propre au transporteur.
export const standardShipmentDraftSchema = commonShipmentDraftSchema
    .extend({
    category: z.literal('standard'),
    type: shipmentTypeSchema.extract(['standard_shop', 'standard_home', 'befret_home']),
    businessModel: z.literal('immediate_payment'),
    transportProvider: z.literal('dpd'),
    status: initialStatusSchema.extend({ current: z.literal('ready_for_deposit') }),
    // `ready_for_deposit` → `dpd_collection` : la file « collecte DPD ».
    currentPhase: z.literal('dpd_collection'),
    standardData: standardShipmentDataDraftSchema,
})
    .strict();
export const heavyShipmentDraftSchema = commonShipmentDraftSchema
    .extend({
    category: z.literal('heavy'),
    type: z.literal('heavy_specialist'),
    businessModel: z.literal('quote_based'),
    transportProvider: z.literal('specialist_partner'),
    status: initialStatusSchema.extend({ current: z.literal('heavy_request_received') }),
    // `heavy_request_received` → `heavy_processing` : la file « devis lourd ».
    currentPhase: z.literal('heavy_processing'),
    heavyData: heavyShipmentDataDraftSchema,
})
    .strict();
/**
 * Forme persistée stricte produite par `buildShipmentDraft`. La catégorie discrimine
 * les champs standard et lourd avant tout enrichissement Stripe, DPD ou backoffice.
 */
export const unifiedShipmentDraftSchema = z
    .discriminatedUnion('category', [standardShipmentDraftSchema, heavyShipmentDraftSchema])
    .superRefine((shipment, context) => {
    // #1706 — contrepartie PERSISTÉE de la règle du formulaire : un brouillon
    // en livraison à domicile ne peut pas être écrit sans l'adresse qui
    // permettra de le livrer. La décision reste unique
    // (`receiverStreetIsRequired`) ; seul son point d'application change.
    if (receiverStreetIsRequired(shipment.parcelInfo.deliveryMethod) &&
        !shipment.customerInfo.receiver.address.street) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['customerInfo', 'receiver', 'address', 'street'],
            message: 'Rue requise',
        });
    }
});
/** Contrat exact renvoyé par la callable, shipment canonique inclus. */
export const createShipmentDraftResultSchema = z
    .object({
    shipmentId: z.string().min(1),
    trackingNumber: z.string().min(1),
    category: shipmentCategorySchema,
    shipment: unifiedShipmentDraftSchema,
    idempotentReplay: z.boolean(),
})
    .strict();
